/**
 * web/src/components/shared/Select.tsx
 * Native <select> primitive matching `.select` from design_files/styles.css.
 * For the heavier Radix Select (with custom popover styling), see
 * web/src/components/ui/select.tsx (Plan 05). Native is fine for plain
 * dropdowns (status filter, type filter) where we don't need a custom
 * popover — and avoids the a11y heavy-lift Radix is reserved for.
 */
import { forwardRef } from 'react';
import type { SelectHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

/** Public props for Select — inherits every native <select> attribute. */
export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

/**
 * Hand-rolled native Select primitive.
 * Side effects: none — pure styled wrapper around a native <select>.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, children, ...props }, ref) => (
    <select
      ref={ref}
      className={cn(
        'rounded-md border bg-bg px-2.5 py-1.5 text-[13px] text-text outline-none transition-colors duration-100',
        'border-border-strong cursor-pointer',
        'focus:border-accent focus:ring-[3px] focus:ring-accent-soft',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';
